import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useApiCall from "./useApiCall";

/**
 * Custom hook to handle DELETE requests with a JSON body using useApiCall.
 *
 * @param {string} url - The API endpoint URL for the DELETE request.
 * @returns {object} - Object containing response data,
 * loading state, error state, and request function.
 */
const useDeleteRequest = (url) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const tokens = useSelector((state) => state?.tokens?.value);
  const { makeRequest, isLoading } = useApiCall();

  /**
   * Sends a DELETE request with optional body and URL parameters.
   *
   * @param {object} body - Body to send in the DELETE request (e.g. { app_id: 123, ids: [1,2] }).
   * @param {string} [urlsParam=""] - Optional string for URL query params or suffix.
   * @returns {Promise<any>} - The response data from the API.
   * @throws {Error} - Throws an error if the request fails.
   */
  const sendDeleteRequest = useCallback(
    async (body, urlsParam = "") => {
      setError(null);

      try {
        const response = await makeRequest(url + urlsParam, {
          method: "DELETE",
          authTokens: tokens,
          body,
        });

        setData(response || []);
        return response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [url, tokens, makeRequest]
  );

  return { data, loading: isLoading, error, sendDeleteRequest };
};

export default useDeleteRequest;
